import { useState, useEffect, useRef, type SyntheticEvent } from 'react';
import { Slide, Snackbar, Alert } from '@mui/material';
import type { SnackbarCloseReason } from '@mui/material/Snackbar';
import { useAppContext } from '../../provider/contexts';
import { defaultNotificationValue } from '../../types';

function NotificationAlert() {
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const { notification, setNotification } = useAppContext();
    const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const closeNotificationHandler = (_event?: SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
        if (reason === 'clickaway') return;
        setIsNotificationOpen(false);
        if (resetTimeoutRef.current) {
            clearTimeout(resetTimeoutRef.current);
        }
        resetTimeoutRef.current = setTimeout(() => {
            setNotification(defaultNotificationValue);
            resetTimeoutRef.current = null;
        }, 300);
    };

    useEffect(() => {
        if (notification.message) {
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
                resetTimeoutRef.current = null;
            }
            setIsNotificationOpen(true);
            return;
        }
        setIsNotificationOpen(false);
    }, [notification]);

    useEffect(
        () => () => {
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }
        },
        [],
    );

    // Custom sanitizer to allow safe HTML and prevent XSS
    const sanitizeHTML = (html: string) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        // Remove <script> and <iframe> tags completely
        const removeTags = ['script', 'iframe', 'object', 'embed', 'style'];
        removeTags.forEach((tag) => {
            const elements = tempDiv.getElementsByTagName(tag);
            while (elements.length > 0) {
                elements[0].parentNode?.removeChild(elements[0]);
            }
        });

        // Allow only specific safe HTML tags (remove all attributes)
        const allowedTags = ['b', 'i', 'u', 'strong', 'em', 'br', 'p'];
        [...tempDiv.getElementsByTagName('*')].forEach((element) => {
            if (!allowedTags.includes(element.tagName.toLowerCase())) {
                element.replaceWith(document.createTextNode(element.textContent ?? ''));
            } else {
                // Remove all attributes to prevent inline JS
                [...element.attributes].forEach((attr) => element.removeAttribute(attr.name));
            }
        });

        return tempDiv.innerHTML;
    };

    // Sanitize the notification message
    const sanitizedMessage = sanitizeHTML(notification.message ?? '');

    return (
        <>
            <Snackbar
                open={isNotificationOpen && Boolean(notification.message)}
                autoHideDuration={6000}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                TransitionComponent={Slide}
                onClose={closeNotificationHandler}
            >
                <Alert severity={notification.severity} sx={{ width: '100%' }} onClose={closeNotificationHandler}>
                    <div dangerouslySetInnerHTML={{ __html: sanitizedMessage }} />
                </Alert>
            </Snackbar>
        </>
    );
}

export default NotificationAlert;
